import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigationType } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { getCategoryLabel } from '../lib/categories';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nombre de produits affichés avant de charger la fournée suivante
const PAGE_SIZE = 24;

// Fournées chargées automatiquement au défilement avant de repasser au bouton.
// Trois fournées = 96 produits, de quoi parcourir longuement sans cliquer,
// tout en gardant le pied de page atteignable.
const CHARGEMENTS_AUTO = 3;

// Mémoire de la liste, le temps d'aller voir une fiche produit et de revenir.
// sessionStorage et non localStorage : cette position n'a de sens que pour
// l'onglet en cours, elle n'a pas à survivre à sa fermeture.
const CLE_ETAT_LISTE = 'afrione_liste_produits';

// Nombre de colonnes de la grille selon la largeur de la fenêtre.
function calculerNombreColonnes() {
  if (typeof window === 'undefined') return 2;
  const largeur = window.innerWidth;
  if (largeur < 640) return 2;
  if (largeur < 1024) return 3;
  if (largeur < 1280) return 4;
  return 5;
}

function useNombreColonnes() {
  const [nombre, setNombre] = useState(calculerNombreColonnes);
  useEffect(() => {
    const auRedimensionnement = () => setNombre(calculerNombreColonnes());
    window.addEventListener('resize', auRedimensionnement);
    return () => window.removeEventListener('resize', auRedimensionnement);
  }, []);
  return nombre;
}

export default function Products() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const typeNavigation = useNavigationType();

  // Au retour depuis une fiche produit (bouton Précédent), on relit l'état
  // laissé derrière soi. Lu une seule fois, à la construction du composant :
  // le faire dans un effet provoquerait un affichage à 24 produits puis un saut.
  const [etatRepris] = useState(() => {
    if (typeNavigation !== 'POP') return null;
    try {
      const brut = sessionStorage.getItem(CLE_ETAT_LISTE);
      if (!brut) return null;
      const etat = JSON.parse(brut);
      // La catégorie vient de l'URL : si elle a changé, l'état ne correspond plus.
      const categorieUrl = searchParams.get('category') || 'all';
      return etat.categorie === categorieUrl ? etat : null;
    } catch (e) {
      return null;
    }
  });

  const [searchQuery, setSearchQuery] = useState(etatRepris?.recherche ?? "");
  const [sortBy, setSortBy] = useState(etatRepris?.tri ?? 'default');
  const [visibleCount, setVisibleCount] = useState(etatRepris?.visibleCount ?? PAGE_SIZE);

  const getLocalizedName = (p) => (language === 'fr' ? p.name_fr : p.name_en);

  // Revenir au début de la liste quand les filtres changent réellement.
  // On compare les filtres à leur valeur précédente plutôt que de sauter le
  // « premier rendu » : en mode strict, React exécute chaque effet deux fois
  // au montage, et un garde à usage unique était consommé par le premier
  // passage — le second remettait la liste à 24 et effaçait l'état repris.
  const signatureFiltres = `${selectedCategory}|${searchQuery}|${sortBy}`;
  const signaturePrecedente = useRef(signatureFiltres);
  useEffect(() => {
    if (signaturePrecedente.current === signatureFiltres) return;
    signaturePrecedente.current = signatureFiltres;
    setVisibleCount(PAGE_SIZE);
  }, [signatureFiltres]);

  // Mémorisation : on écrit l'état au démontage, c'est-à-dire au moment où le
  // visiteur quitte la liste. Une copie vivante dans une ref évite de réécrire
  // sessionStorage à chaque défilement.
  const etatCourant = useRef(null);
  etatCourant.current = { categorie: selectedCategory, recherche: searchQuery, tri: sortBy, visibleCount };

  // La position est relevée pendant le défilement, pas au démontage : à cet
  // instant ScrollToTop a déjà remis la page en haut (son effet de mise en
  // page s'exécute avant les effets passifs), et on n'enregistrerait que des
  // zéros. Écrire dans une ref ne provoque aucun rendu.
  const dernierScroll = useRef(0);
  useEffect(() => {
    const relever = () => { dernierScroll.current = window.scrollY; };
    relever();
    window.addEventListener('scroll', relever, { passive: true });
    return () => window.removeEventListener('scroll', relever);
  }, []);

  useEffect(() => () => {
    try {
      sessionStorage.setItem(CLE_ETAT_LISTE, JSON.stringify({
        ...etatCourant.current,
        scrollY: dernierScroll.current,
      }));
    } catch (e) {
      // Stockage indisponible : on perd la position, sans conséquence.
    }
  }, []);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        // Error handled silently
      }
    };
    fetchCategories();
  }, []);
  
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory && selectedCategory !== 'all' 
          ? `${API}/products?category=${selectedCategory}`
          : `${API}/products`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCategory]);
  
  // Défilement horizontal des catégories (flèches desktop)
  const categoriesRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = categoriesRef.current;
    if (!el) return;
    const marge = 8; // tolérance pour les arrondis de pixels
    setCanScrollLeft(el.scrollLeft > marge);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - marge);
  }, []);

  useEffect(() => {
    const el = categoriesRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, categories]);

  const scrollCategories = (direction) => {
    const el = categoriesRef.current;
    if (!el) return;
    // On défile d'environ 80 % de la largeur visible pour garder un repère
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  // La recherche porte toujours sur l'ensemble du catalogue. Rester enfermé
  // dans la catégorie affichée faisait répondre « aucun produit trouvé » alors
  // que le produit existait dans une autre famille : le client en concluait
  // que la boutique ne le vendait pas.
  const handleSearchChange = (valeur) => {
    setSearchQuery(valeur);
    if (valeur && selectedCategory !== 'all') {
      handleCategoryChange('all');
    }
  };

  // Filtrer les produits par recherche
  const searchedProducts = products.filter(product => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    // Le nom de la catégorie fait partie des critères : chercher « poulet »
    // doit remonter la « Poule » et les « Ailes », qui ne contiennent pas le mot.
    const champs = [
      product.name_fr,
      product.name_en,
      product.sku,
      product.description_fr,
      product.brand,
      product.format,
      getCategoryLabel(product.category, language),
    ];
    return champs.some((champ) => (champ || '').toLowerCase().includes(query));
  });

  // Tri (copie du tableau : sort() modifie sur place)
  const filteredProducts = [...searchedProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price_asc': return a.price - b.price;
      case 'price_desc': return b.price - a.price;
      case 'name_asc':
        return getLocalizedName(a).localeCompare(getLocalizedName(b), 'fr');
      default: return 0; // ordre du catalogue
    }
  });

  // Affichage progressif : afficher plusieurs centaines de cartes d'un coup
  // alourdit la page et décourage le parcours.
  const visibleProducts = filteredProducts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProducts.length;

  // Chargement au défilement, mais pas à l'infini : au-delà de CHARGEMENTS_AUTO
  // fournées, on rend la main au bouton. Sans cette limite, le pied de page
  // s'éloignerait à chaque approche et deviendrait inatteignable — or il porte
  // les mentions légales, les CGV et le contact.
  const chargementsAuto = Math.floor(visibleCount / PAGE_SIZE) - 1;
  const autoDisponible = hasMore && chargementsAuto < CHARGEMENTS_AUTO;

  // L'observateur s'attache par une ref de rappel, appelée par React au moment
  // exact où la sentinelle entre dans le DOM. Un effet ne suffisait pas : au
  // changement de catégorie, la grille disparaît le temps du chargement, donc
  // la sentinelle aussi ; l'effet s'exécutait sur un élément absent et n'était
  // pas relancé à son retour, laissant le défilement sans observateur.
  const observateur = useRef(null);
  const noeudSentinelle = useRef(null);
  const autoDisponibleRef = useRef(autoDisponible);
  autoDisponibleRef.current = autoDisponible;

  const sentinelle = useCallback((noeud) => {
    if (observateur.current) {
      observateur.current.disconnect();
      observateur.current = null;
    }
    noeudSentinelle.current = noeud;
    if (!noeud) return;
    // Le déclenchement se fait 600 px avant la sentinelle, pour que les
    // produits soient là quand le visiteur arrive en bas.
    observateur.current = new IntersectionObserver(
      (entrees) => {
        if (entrees[0].isIntersecting && autoDisponibleRef.current) {
          setVisibleCount((n) => n + PAGE_SIZE);
        }
      },
      { rootMargin: '600px 0px' }
    );
    observateur.current.observe(noeud);
  }, []);

  // Après chaque fournée, on ré-observe : si la sentinelle est restée dans la
  // zone de déclenchement, aucun nouvel événement ne serait émis et le
  // chargement s'arrêterait là.
  useEffect(() => {
    const obs = observateur.current;
    const noeud = noeudSentinelle.current;
    if (!obs || !noeud) return;
    obs.unobserve(noeud);
    obs.observe(noeud);
  }, [visibleCount, autoDisponible]);

  // Retour arrière : une fois les produits rendus, on retrouve la position
  // quittée. useLayoutEffect place le défilement avant le premier affichage,
  // donc sans voir passer le haut de la page. Les images réservent déjà leur
  // hauteur (image_width/height), la mise en page est donc définitive ici.
  const scrollARestaurer = useRef(etatRepris?.scrollY ?? null);
  useLayoutEffect(() => {
    if (scrollARestaurer.current == null || loading || visibleProducts.length === 0) return;
    const y = scrollARestaurer.current;
    scrollARestaurer.current = null;
    window.scrollTo(0, y);
  }, [loading, visibleProducts.length]);

  // Grille décalée : les produits sont distribués en colonnes à tour de rôle.
  // Un masonry CSS (`columns`) rééquilibrerait toutes les colonnes à chaque
  // « Voir plus », faisant sauter les produits déjà parcourus ; ici l'indice
  // d'un produit ne change pas, donc sa place non plus.
  const nombreColonnes = useNombreColonnes();
  const colonnes = Array.from({ length: nombreColonnes }, () => []);
  visibleProducts.forEach((produit, index) => {
    // Le rang global est conservé : réparti en colonnes, un produit perdrait
    // sa place dans l'ordre d'affichage, et on ne saurait plus lesquels
    // occupent le premier écran.
    colonnes[index % nombreColonnes].push({ produit, rang: index });
  });

  // Trois rangées de cartes couvrent le premier écran sur un grand écran, et
  // davantage sur mobile où les colonnes sont moins nombreuses.
  const RANGS_PRIORITAIRES = nombreColonnes * 3;
  
  const getCategoryName = (cat) => {
    return language === 'fr' ? cat.name_fr : cat.name_en;
  };
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="products-page">
      <SEO
        title={language === 'en'
          ? 'All our African products - AfriOne BD'
          : 'Tous nos produits africains - AfriOne BD'}
        description={language === 'en'
          ? 'African fish, meat, spices, dry goods and frozen food delivered in Liège and across Belgium. Order online from AfriOne BD.'
          : 'Poissons, viandes, épices, produits secs et surgelés africains livrés à Liège et partout en Belgique. Commandez en ligne chez AfriOne BD.'}
        ogTitle={language === 'en' ? 'All our African products - AfriOne BD' : 'Tous nos produits africains - AfriOne BD'}
        ogDescription={language === 'en'
          ? 'African grocery online: fish, meat, spices and fresh products delivered in Liège and Belgium.'
          : 'Épicerie africaine en ligne : poissons, viandes, épices et produits frais livrés à Liège et en Belgique.'}
        keywords={language === 'en'
          ? 'african products, african fish, chicken, cassava, plantain, okra, african spices'
          : 'produits africains, poisson africain, poulet, manioc, plantain, gombo, épices africaines'}
        canonical="https://afrionebd.eu/products"
      />
      {/* Header */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">{language === 'en' ? 'Collection' : 'Collection'}</span>
            <h1 className="text-3xl lg:text-5xl font-bold font-heading text-text_primary mt-2 tracking-tight" data-testid="products-title">
              {t('allProducts')}
            </h1>
          </motion.div>
        </div>
      </section>
      
      {/* Categories Filter + Search Bar */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={language === 'fr' ? "Rechercher dans tout le catalogue..." : "Search the whole catalogue..."}
                aria-label={language === 'fr' ? 'Rechercher un produit' : 'Search for a product'}
                className="w-full px-6 py-3 pl-12 rounded-full border-2 border-gray-200 focus:border-primary focus:outline-none transition-all"
              />
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  aria-label={language === 'fr' ? 'Effacer la recherche' : 'Clear search'}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Tri */}
          <div className="flex justify-center mb-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label={language === 'fr' ? 'Trier les produits' : 'Sort products'}
              className="px-4 py-2 text-sm rounded-full border-2 border-gray-200 bg-white focus:border-primary focus:outline-none cursor-pointer"
              data-testid="sort-select"
            >
              <option value="default">{language === 'fr' ? 'Trier : par défaut' : 'Sort: default'}</option>
              <option value="price_asc">{language === 'fr' ? 'Prix croissant' : 'Price: low to high'}</option>
              <option value="price_desc">{language === 'fr' ? 'Prix décroissant' : 'Price: high to low'}</option>
              <option value="name_asc">{language === 'fr' ? 'Nom (A-Z)' : 'Name (A-Z)'}</option>
            </select>
          </div>

          {/* Categories - défilement horizontal.
              Sur mobile le doigt suffit ; sur desktop la barre de défilement est
              masquée et la molette ne défile pas horizontalement, d'où les flèches. */}
          <div className="relative">
            {canScrollLeft && (
              <>
                <div className="hidden md:block absolute left-0 top-0 bottom-2 w-12 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
                <button
                  onClick={() => scrollCategories(-1)}
                  aria-label={language === 'en' ? 'Previous categories' : 'Voir les catégories précédentes'}
                  className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 z-20 w-9 h-9 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-text_primary hover:bg-secondary hover:scale-105 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </>
            )}

            <div
              ref={categoriesRef}
              className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide scroll-smooth"
            >
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === cat.id
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-secondary/50 text-text_secondary hover:bg-secondary'
                  }`}
                  data-testid={`category-filter-${cat.id}`}
                >
                  {getCategoryName(cat)}
                </button>
              ))}
            </div>

            {canScrollRight && (
              <>
                <div className="hidden md:block absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
                <button
                  onClick={() => scrollCategories(1)}
                  aria-label={language === 'en' ? 'Next categories' : 'Voir les catégories suivantes'}
                  className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 z-20 w-9 h-9 items-center justify-center rounded-full bg-white shadow-md border border-gray-200 text-text_primary hover:bg-secondary hover:scale-105 transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </section>
      
      {/* Products Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-start gap-x-2 sm:gap-x-3">
              {[...Array(nombreColonnes)].map((_, i) => (
                <div key={`squelette-${i}`} className="flex-1 min-w-0 animate-pulse pb-5">
                  <div className="w-full aspect-square bg-gray-200 mb-2" />
                  <div className="h-3 bg-gray-200 w-3/4 mb-2" />
                  <div className="h-5 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-text_secondary text-lg">
                {searchQuery 
                  ? (language === 'fr' ? 'Aucun produit trouvé pour votre recherche' : 'No products found for your search')
                  : 'Aucun produit trouvé'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-4 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary_hover"
                >
                  {language === 'fr' ? 'Effacer la recherche' : 'Clear search'}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <p className="text-text_secondary">
                  {language === 'fr'
                    ? `${visibleProducts.length} produit${visibleProducts.length > 1 ? 's' : ''} affiché${visibleProducts.length > 1 ? 's' : ''} sur ${filteredProducts.length}`
                    : `Showing ${visibleProducts.length} of ${filteredProducts.length} products`
                  }
                </p>
              </div>
              <div className="flex items-start gap-x-2 sm:gap-x-3" data-testid="products-grid">
                {colonnes.map((colonne, indexColonne) => (
                  <div key={`colonne-${indexColonne}`} className="flex-1 min-w-0">
                    {colonne.map(({ produit, rang }) => (
                      <ProductCard
                        key={produit.id}
                        product={produit}
                        prioritaire={rang < RANGS_PRIORITAIRES}
                      />
                    ))}
                  </div>
                ))}
              </div>

              {/* Sentinelle : son approche déclenche la fournée suivante. */}
              <div ref={sentinelle} aria-hidden="true" className="h-px" />

              {/* Pendant le chargement automatique, on annonce la suite plutôt
                  que d'afficher un bouton que personne n'aura à toucher. */}
              {autoDisponible && (
                <p className="mt-10 text-center text-sm text-text_secondary" role="status">
                  {language === 'fr'
                    ? `Chargement des produits suivants… (${filteredProducts.length - visibleProducts.length} restants)`
                    : `Loading more products… (${filteredProducts.length - visibleProducts.length} left)`}
                </p>
              )}

              {hasMore && !autoDisponible && (
                <div className="mt-12 text-center">
                  <button
                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                    className="px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all hover:scale-105 shadow-md"
                    data-testid="load-more-button"
                  >
                    {language === 'fr'
                      ? `Voir plus de produits (${filteredProducts.length - visibleProducts.length} restants)`
                      : `Load more products (${filteredProducts.length - visibleProducts.length} left)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
